import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Permissions } from '@app/core/guard/Permissions';
import { Option } from '@interfaces/common/option';
import { CertificationSealResponse } from '@interfaces/email-signatures/responses/certification-seal.response';
import { UserSignatureProfileResponse } from '@interfaces/email-signatures/responses/user-signature-profile.response';
import { PermissionsService } from '@services/helpers/permissions.service';
import { UserSignatureProfileHttpService } from '@services/http/users/user-signature-profile-http.service';
import { describeBackendError, isForbiddenError } from '@src/app/core/utils/backend-error.utils';
import { SharedModule } from '@src/app/shared/shared.module';
import { Observable, of } from 'rxjs';
import { first } from 'rxjs/operators';

/** Até quantos caracteres cabem em Cargo e em Área (mesmo limite do backend). */
export const SIGNATURE_FIELD_MAX_LENGTH = 100;

/**
 * Em que pé está a seção:
 * - `new`: usuário ainda não salvo; cargo e área editáveis, selos desabilitados;
 * - `loading`: buscando o perfil do usuário em edição;
 * - `ready`: perfil carregado, tudo editável;
 * - `error`: o GET falhou; a seção avisa e não grava nada (gravar apagaria o perfil que não veio);
 * - `hidden`: quem edita não tem permissão (403); a seção some sem erro.
 */
type SignatureFormState = 'new' | 'loading' | 'ready' | 'error' | 'hidden';

/** O que estava gravado quando a seção carregou, para saber se algo mudou. */
interface SignatureSnapshot {
  jobTitle: string;
  department: string;
  sealIds: string[];
}

/**
 * Seção **Assinatura de e-mail** do cadastro de usuário (card 3456): cargo, área e selos de
 * certificação que saem na assinatura dos e-mails de RFQ ao banco e do Relatório de Posição.
 *
 * Fica fora do `user-register` (que já passa de 1.300 linhas): o pai só chama `load(userId)` na
 * edição e `save(userId)` depois de gravar o usuário.
 *
 * Os selos vêm do próprio perfil (`availableSeals`), não do catálogo: quem cadastra usuário pode
 * não ter `CERTIFICATIONSEAL_LIST`. Por isso, no usuário novo (sem id ainda), os selos ficam
 * desabilitados até o primeiro salvar.
 */
@Component({
  selector: 'app-user-email-signature-form',
  standalone: true,
  imports: [CommonModule, FormsModule, SharedModule],
  templateUrl: './user-email-signature-form.component.html',
  styleUrls: ['./user-email-signature-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserEmailSignatureFormComponent {
  private readonly profileHttp = inject(UserSignatureProfileHttpService);

  /** Gravar o perfil exige `USER_UPDATE` (mesma permissão das duas rotas no backend). */
  private readonly canEditProfile = toSignal(
    inject(PermissionsService).hasPermission(Permissions.User.UPDATE),
    { initialValue: false }
  );

  /**
   * O pai está editando um usuário existente? Enquanto o `load` não chega, a seção mostra o
   * carregando em vez de abrir como usuário novo.
   */
  @Input() set editing(value: boolean) {
    if (value && this.state() === 'new') this.state.set('loading');
  }

  readonly fieldMaxLength = SIGNATURE_FIELD_MAX_LENGTH;

  readonly state = signal<SignatureFormState>('new');
  readonly loadError = signal<string | null>(null);

  readonly jobTitle = signal('');
  readonly department = signal('');
  /** Selos marcados, na ordem da fileira. */
  readonly sealIds = signal<string[]>([]);
  readonly availableSeals = signal<CertificationSealResponse[]>([]);

  private snapshot: SignatureSnapshot | null = null;

  readonly isVisible = computed(() => this.canEditProfile() && this.state() !== 'hidden');

  readonly sealOptions = computed<Option<CertificationSealResponse>[]>(() =>
    this.availableSeals().map(seal => ({ label: seal.name, value: seal.id, data: seal }))
  );

  /** Selos escolhidos, na ordem da fileira, para a prévia. */
  readonly selectedSeals = computed(() => {
    const byId = new Map(this.availableSeals().map(seal => [seal.id, seal]));
    return this.sealIds()
      .map(id => byId.get(id))
      .filter((seal): seal is CertificationSealResponse => !!seal && !!seal.imageUrl);
  });

  /** Busca o perfil do usuário em edição e os selos ativos para a escolha. */
  load(userId: string): void {
    if (!userId || !this.canEditProfile()) return;

    this.state.set('loading');
    this.loadError.set(null);

    this.profileHttp
      .getProfile(userId)
      .pipe(first())
      .subscribe({
        next: profile => this.applyProfile(profile),
        error: error => {
          if (isForbiddenError(error)) {
            this.state.set('hidden');
            return;
          }
          this.state.set('error');
          this.loadError.set(
            `Não foi possível carregar a assinatura de e-mail: ${describeBackendError(error)} Abra o usuário de novo para tentar outra vez; o que está gravado não é alterado.`
          );
        },
      });
  }

  /**
   * Grava o estado atual. Devolve `of(null)` sem chamar a API quando não há o que gravar: seção
   * escondida, perfil que não carregou, nada preenchido num usuário sem perfil, ou nada mudou.
   */
  save(userId: string): Observable<unknown> {
    if (!userId || !this.isVisible()) return of(null);
    if (this.state() === 'loading' || this.state() === 'error') return of(null);
    if (!this.hasChanges()) return of(null);

    return this.profileHttp.updateProfile(userId, {
      jobTitle: this.jobTitle().trim() || null,
      department: this.department().trim() || null,
      sealIds: this.sealIds(),
    });
  }

  /** Algo mudou desde a carga (ou, no usuário novo, algo foi preenchido)? */
  hasChanges(): boolean {
    const current: SignatureSnapshot = {
      jobTitle: this.jobTitle().trim(),
      department: this.department().trim(),
      sealIds: this.sealIds(),
    };
    const base = this.snapshot ?? { jobTitle: '', department: '', sealIds: [] };

    return (
      current.jobTitle !== base.jobTitle ||
      current.department !== base.department ||
      current.sealIds.join('|') !== base.sealIds.join('|')
    );
  }

  /**
   * Mantém a ordem em que a pessoa marcou os selos: os que continuam marcados ficam onde estavam e
   * os novos entram no fim da fileira.
   */
  onSealsChange(ids: string[] | null): void {
    const next = ids ?? [];
    const kept = this.sealIds().filter(id => next.includes(id));
    const added = next.filter(id => !kept.includes(id));
    this.sealIds.set([...kept, ...added]);
  }

  private applyProfile(profile: UserSignatureProfileResponse | null): void {
    const available = profile?.availableSeals ?? [];
    const availableIds = new Set(available.map(seal => seal.id));
    // Selo excluído ou inativado depois de marcado não está mais na escolha: sai da fileira.
    const sealIds = (profile?.sealIds ?? []).filter(id => availableIds.has(id));

    this.availableSeals.set(available);
    this.jobTitle.set(profile?.jobTitle ?? '');
    this.department.set(profile?.department ?? '');
    this.sealIds.set(sealIds);
    this.snapshot = {
      jobTitle: (profile?.jobTitle ?? '').trim(),
      department: (profile?.department ?? '').trim(),
      sealIds: profile?.sealIds ?? [],
    };
    this.state.set('ready');
  }
}
